import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { buildConfig } from '../config/buildConfig';
import { createNaaAuthProvider, type AccessTokenProvider } from './naaAuthProvider';

export type AuthStatus = 'initializing' | 'authenticated' | 'error';

export type AuthContextValue = {
  status: AuthStatus;
  getAccessToken: AccessTokenProvider;
  error: unknown;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('initializing');
  const [error, setError] = useState<unknown>(null);
  const [getAccessToken] = useState(() => createNaaAuthProvider(buildConfig));

  useEffect(() => {
    getAccessToken()
      .then(() => setStatus('authenticated'))
      .catch((authenticationError: unknown) => {
        setError(authenticationError);
        setStatus('error');
      });
  }, [getAccessToken]);

  if (status === 'initializing') {
    return <div>Signing you in...</div>;
  }

  if (status === 'error') {
    return <div>We could not sign you in to the catering service.</div>;
  }

  return (
    <AuthContext.Provider value={{ status, getAccessToken, error }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider.');
  }

  return context;
}
